import { useCallback, useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { DEMO_FALLBACK, matchDemoEntry } from '../demo/demoScript';
import type { Message } from '../types/chat';

/**
 * Plays back a scripted Demo Mode reply with the same rhythm as a live
 * answer: a short pause, one or more "thinking" status lines, then the answer
 * streamed word by word into an assistant bubble. Never touches the network.
 */

export type DemoPhase = 'idle' | 'thinking' | 'streaming';

const INITIAL_PAUSE_MS = 600;
const REFUSAL_PAUSE_MS = 250;
const STATUS_MS = 900;
const WORD_MS = 18;
const REFUSAL_WORD_MS = 6;

interface UseDemoChatOptions {
  setMessages: Dispatch<SetStateAction<Message[]>>;
  onCrisis?: () => void;
}

export default function useDemoChat({ setMessages, onCrisis }: UseDemoChatOptions) {
  const [demoStatus, setDemoStatus] = useState<string | null>(null);
  const [demoPhase, setDemoPhase] = useState<DemoPhase>('idle');
  const timersRef = useRef<number[]>([]);
  const cancelledRef = useRef(false);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(resolve, ms);
        timersRef.current.push(id);
      }),
    []
  );

  // Clear any pending timers if the component unmounts mid-reply.
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
    };
  }, []);

  const runDemoReply = useCallback(
    async (question: string, assistantMsgId: string) => {
      const entry = matchDemoEntry(question);
      const answer = entry?.answer ?? DEMO_FALLBACK;
      const statuses = entry?.statuses ?? [];
      const wordDelay = entry?.isRefusal ? REFUSAL_WORD_MS : WORD_MS;

      setDemoPhase('thinking');
      await sleep(entry?.isRefusal ? REFUSAL_PAUSE_MS : INITIAL_PAUSE_MS);
      if (cancelledRef.current) return;

      for (const status of statuses) {
        setDemoStatus(status);
        await sleep(STATUS_MS);
        if (cancelledRef.current) return;
      }
      setDemoStatus(null);

      setDemoPhase('streaming');
      setMessages((prev) => [
        ...prev,
        {
          id: assistantMsgId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isStreaming: true,
          isCrisis: entry?.isCrisis,
        },
      ]);

      // Stream word by word, but pace by wall-clock time rather than by tick:
      // browsers throttle timers in background tabs to ~1/sec, and a
      // tick-driven loop would then crawl. Each tick shows every word that
      // is "due" by now, so the answer always lands on schedule.
      const words = answer.split(/(\s+)/); // keeps whitespace tokens at odd indices
      const wordCount = Math.ceil(words.length / 2);
      const startedAt = Date.now();
      let shownWords = 0;
      while (shownWords < wordCount) {
        await sleep(wordDelay);
        if (cancelledRef.current) return;
        const due = Math.min(wordCount, Math.floor((Date.now() - startedAt) / wordDelay) + 1);
        if (due <= shownWords) continue;
        shownWords = due;
        const snapshot = words.slice(0, shownWords * 2).join('');
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMsgId ? { ...m, content: snapshot } : m))
        );
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId ? { ...m, content: answer, isStreaming: false } : m
        )
      );
      if (entry?.isCrisis) onCrisis?.();
      setDemoPhase('idle');
    },
    [setMessages, onCrisis, sleep]
  );

  return { runDemoReply, demoStatus, demoPhase };
}
